import {jwtDecode} from 'jwt-decode'

type AccessRuleNode = {
  value: string
  children?: {
    name: string
    selectedValues: AccessRuleNode[]
  }[]
}
type AccessRules = {
  name: string
  selectedValues: AccessRuleNode[]
}[]

type rulesType = {
  role: string
  accessRules?: AccessRules
}

export type AccessRulesAccess = [applications?: string[], countries?: string[]]

// Super Admin has unrestricted access. Application Admin is unrestricted by country.
export function accessRulesParsing(): AccessRulesAccess {
  const idToken = localStorage.getItem('msalAccessToken')

  const accessRules = idToken ? jwtDecode<{accessRules?: string}>(idToken)?.accessRules : null
  if (!accessRules) {
    return [[], []]
  }

  const rules: rulesType = JSON.parse(accessRules)
  if (rules?.role === 'super-admin') {
    return [undefined, undefined]
  }

  // Migrate legacy admin role to B2B application admin access
  if (rules?.role === 'admin') {
    return [['B2B'], undefined]
  }

  const selectedApplications = rules?.accessRules?.[0]?.selectedValues ?? []
  const applications = selectedApplications.map(application => application.value)
  if (rules?.role === 'app-admin') {
    return [applications, undefined]
  }

  if (rules?.role === 'app-member') {
    const countries = selectedApplications.flatMap(application => getLeafValues(application))
    return [applications, countries]
  }

  // Migrate legacy member role to B2B application app-member access
  if (rules?.role === 'member') {
    return [['B2B'], getLegacyMemberCountries(rules.accessRules)]
  }

  return [[], []]
}

function getLeafValues(node: AccessRuleNode): string[] {
  if (!node.children?.length) {
    return [node.value]
  }

  return node.children.flatMap(child => child.selectedValues.flatMap(getLeafValues))
}

function getLegacyMemberCountries(accessRules?: AccessRules): string[] {
  return (accessRules?.[0]?.selectedValues ?? []).flatMap(region =>
    (region.children ?? []).flatMap(child => child.selectedValues.map(country => country.value))
  )
}

export function getValuesWithAccess(values: string[], allowedValues?: string[]) {
  if (!allowedValues) {
    return values
  }
  const allowedValuesSet = new Set(allowedValues)
  return values.filter(value => allowedValuesSet.has(value))
}
